import { useEffect, useState } from "react";

// Motion is opt-in: no matchMedia (tests, old browsers) or reduced motion means static.
export function canAnimate() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function finePointer() {
  return (
    typeof window !== "undefined" &&
    !!window.matchMedia?.("(hover: hover) and (pointer: fine)").matches
  );
}

export const clamp = (v, min = 0, max = 1) => Math.min(max, Math.max(min, v));
export const lerp = (a, b, t) => a + (b - a) * t;
export const ease = (t) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

export function seeded(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

// True while the element is on screen; always true where it cannot be observed.
export function useInView(ref, margin = "0px") {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (!window.IntersectionObserver) {
      setInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin: margin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, margin]);
  return inView;
}

// SMIL keeps running under reduced motion and off screen unless paused explicitly.
export function useSvgPlayback(ref) {
  const inView = useInView(ref, "80px");
  useEffect(() => {
    const svg = ref.current;
    if (!svg?.pauseAnimations) return;
    if (!canAnimate()) {
      svg.pauseAnimations();
      svg.setCurrentTime?.(1.2);
      return;
    }
    if (inView) svg.unpauseAnimations();
    else svg.pauseAnimations();
  }, [ref, inView]);
  return inView;
}
